import { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit } from "@mui/icons-material";
import {
  useContacts,
  useCreateContact,
  useDeleteContact,
  useUpdateContact,
} from "@/api/hooks/contacts";
import { CONTACT_ROLE_LABELS } from "@/api/labels";
import type { Contact } from "@/api/types";
import ContactForm, {
  EMPTY_CONTACT_DRAFT,
  contactDraftToPayload,
  type ContactDraft,
} from "./ContactForm";

interface Props {
  siteId: string;
}

function fromContact(c: Contact): ContactDraft {
  return {
    role: c.role,
    name: c.name,
    email: c.email ?? "",
    phone: c.phone ?? "",
    title: c.title ?? "",
    notes: c.notes ?? "",
  };
}

export default function ContactsSection({ siteId }: Props) {
  const { data, isLoading } = useContacts(siteId);
  const createMutation = useCreateContact(siteId);
  const deleteMutation = useDeleteContact(siteId);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography variant="overline" color="text.secondary">
            Key contacts
          </Typography>
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() => {
              setAdding(true);
              setEditingId(null);
            }}
            disabled={adding}
          >
            Add contact
          </Button>
        </Box>

        {adding && (
          <ContactForm
            initial={EMPTY_CONTACT_DRAFT}
            submitLabel="Add"
            onCancel={() => setAdding(false)}
            onSubmit={async (d) => {
              await createMutation.mutateAsync(contactDraftToPayload(d));
              setAdding(false);
            }}
          />
        )}

        {isLoading ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <CircularProgress size={20} />
          </Box>
        ) : !data || data.length === 0 ? (
          !adding && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 3, textAlign: "center" }}
            >
              No contacts yet.
            </Typography>
          )
        ) : (
          <Stack spacing={1.5} sx={{ mt: adding ? 2 : 0 }}>
            {data.map((c) =>
              editingId === c.id ? (
                <EditContactRow
                  key={c.id}
                  siteId={siteId}
                  contact={c}
                  onDone={() => setEditingId(null)}
                />
              ) : (
                <Box
                  key={c.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                >
                  <Chip
                    size="small"
                    label={CONTACT_ROLE_LABELS[c.role]}
                    color="primary"
                    variant="outlined"
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 500 }}>{c.name}</Typography>
                    {c.title && (
                      <Typography variant="caption" color="text.secondary">
                        {c.title}
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
                      {c.email && (
                        <Box
                          component="a"
                          href={`mailto:${c.email}`}
                          sx={{
                            color: "primary.main",
                            textDecoration: "none",
                            fontSize: "0.875rem",
                          }}
                        >
                          {c.email}
                        </Box>
                      )}
                      {c.phone && (
                        <Typography variant="body2">{c.phone}</Typography>
                      )}
                    </Box>
                    {c.notes && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {c.notes}
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditingId(c.id);
                      setAdding(false);
                    }}
                    aria-label={`Edit ${c.name}`}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (confirm(`Remove ${c.name}?`)) {
                        deleteMutation.mutate(c.id);
                      }
                    }}
                    aria-label={`Delete ${c.name}`}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ),
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function EditContactRow({
  siteId,
  contact,
  onDone,
}: {
  siteId: string;
  contact: Contact;
  onDone: () => void;
}) {
  const updateMutation = useUpdateContact(siteId, contact.id);
  return (
    <ContactForm
      initial={fromContact(contact)}
      submitLabel="Save"
      onCancel={onDone}
      onSubmit={async (d) => {
        await updateMutation.mutateAsync(contactDraftToPayload(d));
        onDone();
      }}
    />
  );
}

